import React, { useState, useEffect } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "prism-react-renderer";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { myFilesGenerateSummary } from "../../../../../services/CommonService";
import { toast } from "react-toastify";
import styled from "styled-components";
import { saveAs } from "file-saver";
import { Document, Paragraph, TextRun, Packer } from "docx";
import { jsPDF } from "jspdf";
import { LuSpeech } from "react-icons/lu";
import { FaRegStopCircle, FaDownload, FaCopy, FaArrowLeft, FaSearchPlus, FaSearchMinus } from "react-icons/fa";
import { SiGooglegemini } from "react-icons/si";

// Styled components for markdown rendering
const MarkdownContainer = styled.div`
  line-height: 1.6;
  color: #333;
  height: 100%;
  overflow-y: auto;
  padding: 15px;

  h1 {
    font-size: 2rem;
    margin: 1.5rem 0 1rem;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid #eee;
  }

  h2 {
    font-size: 1.75rem;
    margin: 1.3rem 0 0.8rem;
  }

  h3 {
    font-size: 1.5rem;
    margin: 1.1rem 0 0.6rem;
  }

  h4,
  h5,
  h6 {
    font-size: 1.25rem;
    margin: 1rem 0 0.5rem;
  }

  p {
    margin: 0.8rem 0;
    font-size: 1rem;
    text-align: justify;
  }

  a {
    color: #0366d6;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ul,
  ol {
    margin: 0.8rem 0;
    padding-left: 2rem;
  }

  li {
    margin: 0.4rem 0;
    text-align: justify;
  }

  blockquote {
    margin: 1rem 0;
    padding: 0 1rem;
    border-left: 0.25rem solid #dfe2e5;
    color: #6a737d;
  }

  pre {
    background-color: #f6f8fa;
    border-radius: 3px;
    padding: 1rem;
    overflow: auto;
    margin: 1rem 0;
  }

  code {
    background-color: rgba(27, 31, 35, 0.05);
    border-radius: 3px;
    padding: 0.2em 0.4em;
    font-size: 85%;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
    display: block;
    overflow-x: auto;
  }

  th,
  td {
    border: 1px solid #dfe2e5;
    padding: 0.5rem 1rem;
  }

  th {
    background-color: #f6f8fa;
    font-weight: 600;
  }

  tr:nth-child(even) {
    background-color: #f6f8fa;
  }

  img {
    max-width: 100%;
  }
  .markdown-content h1 {
    font-size: 1.8rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #2c3e50;
  }
  .action-buttons {
    display: flex;
    gap: 8px;
    margin-top: 15px;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
  }
  .action-btn {
    padding: 5px 10px;
    font-size: 0.8rem;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    border: 1px solid #ddd;
    background-color: #f5f5f5;
    color: #333;
  }

  .action-btn:hover {
    background-color: #e0e0e0;
  }

  .pdf-btn {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    color: #333;
  }

  .pdf-btn:hover {
    background-color: #e0e0e0;
  }

  .docx-btn {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    color: #333;
  }

  .docx-btn:hover {
    background-color: #e0e0e0;
  }

  .speech-btn {
    background-color: #28a745;
    border: 1px solid #28a745;
    color: white;
  }

  .speech-btn:hover {
    background-color: #218838;
  }

  .speech-btn.stop {
    background-color: #dc3545;
    border: 1px solid #dc3545;
  }

  .speech-btn.stop:hover {
    background-color: #c82333;
  }

  .voice-controls {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .form-select {
    font-size: 0.8rem;
    padding: 5px;
    border-radius: 4px;
    border: 1px solid #ddd;
    background-color: #f5f5f5;
    color: #333;
  }

  .form-select:focus {
    outline: none;
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
  }

  .generate-btn {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 5px 15px;
    border-radius: 4px;
    cursor: pointer;
    margin: 15px;
  }

  .generate-btn:hover {
    background-color: #0056b3;
  }

  .generate-btn:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }

  .nav-tabs {
    margin-bottom: 0;
  }

  .nav-tabs .nav-link {
    color: #495057;
    border: none;
    padding: 10px 20px;
    font-weight: 500;
    transition: all 0.3s ease;
  }

  .nav-tabs .nav-link:hover {
    color: #0d6efd;
  }

  .nav-tabs .nav-link.active {
    background-color: rgb(121, 128, 136);
    color: #495057 !important;
    font-weight: bold;
    border-bottom: 3px solid #0d6efd;
  }

  .footer {
    background-color: #f8f9fa;
    border-top: 1px solid #ddd;
    padding: 10px 0;
    text-align: center;
    font-size: 0.9rem;
    color: #6a737d;
    position: fixed;
    bottom: 0;
    width: 100%;
    height: 40px;
    z-index: 1000;
  }

  .rpv-default-layout__body {
    padding-top: 0.5rem;
  }

  @media (max-width: 768px) {
    .action-buttons {
      justify-content: center;
    }

    .col-md-6 {
      height: auto !important;
    }
  }

  .pdf-container {
    position: relative;
    height: calc(100% - 50px);
    display: flex;
    flex-direction: column;
  }

  .pdf-viewer {
    flex: 1;
    overflow: auto;
  }

`;

const DocumentSummary = () => {
  const storedData = sessionStorage.getItem("summaryData");
  const docData = storedData ? JSON.parse(storedData) : null;
  const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const zoomPluginInstance = zoomPlugin();

  const [summaries, setSummaries] = useState({
    short: "",
    medium: "",
    large: ""
  });
  const [activeTab, setActiveTab] = useState("short");
  const [isLoading, setIsLoading] = useState(false);
  const [speechSpeed, setSpeechSpeed] = useState(1);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [voices, setVoices] = useState([]);
  const [zoomLevel, setZoomLevel] = useState(1.0);
  const zoomLevels = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  const synth = window.speechSynthesis;
  let speechQueue = [];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const generateSummary = async (length) => {
    if (!docData?.userId || !docData?.fileId) {
      toast.error("Missing user or file information");
      return;
    }

    setIsLoading(true);
    setActiveTab(length);

    try {
      const payload = {
        user_id: docData.userId,
        file_id: docData.fileId,
        length: length
      };

      const response = await myFilesGenerateSummary(payload);
      setSummaries(prev => ({
        ...prev,
        [length]: response.data?.summary || "No summary available"
      }));
      toast.success(`${length} summary generated successfully!`);
    } catch (error) {
      console.error("Error generating summary:", error);
      toast.error("Failed to generate summary");
      setSummaries(prev => ({
        ...prev,
        [length]: "Error generating summary"
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    if (!text) {
      toast.error('No summary available to copy');
      return;
    }
    const plainText = text.replace(/[*_#`]+/g, '');
    navigator.clipboard
      .writeText(plainText)
      .then(() => toast.success('Summary copied to clipboard!'))
      .catch(() => toast.error('Failed to copy text'));
  };

  const downloadAsPDF = async (content, type) => {
    if (!content) {
      toast.error('No summary available to download');
      return;
    }
    try {
      const plainText = content.replace(/[*_#`]+/g, '');
      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(`${type.charAt(0).toUpperCase() + type.slice(1)} Summary`, 10, 10);
      doc.setFontSize(12);
      const splitText = doc.splitTextToSize(plainText, 190);
      doc.text(splitText, 10, 20);
      doc.save(`${docData?.filePath ? docData.filePath.split('/').pop().split('.')[0] : 'document'}_${type}_summary.pdf`);
      toast.success('Summary downloaded as PDF');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  const downloadAsDocx = async (content, type) => {
    if (!content) {
      toast.error('No summary available to download');
      return;
    }
    try {
      const plainText = content.replace(/[*_#`]+/g, '');
      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `${type.charAt(0).toUpperCase() + type.slice(1)} Summary`,
                    bold: true,
                    size: 24,
                  }),
                ],
                spacing: { after: 200 },
              }),
              ...plainText.split('\n').map(
                (line) =>
                  new Paragraph({
                    children: [new TextRun({ text: line, size: 20 })],
                    spacing: { after: 100 },
                  })
              ),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, `${docData?.filePath ? docData.filePath.split('/').pop().split('.')[0] : 'document'}_${type}_summary.docx`);
      toast.success('Summary downloaded as DOCX');
    } catch (error) {
      console.error('DOCX download error:', error);
      toast.error('Failed to download DOCX');
    }
  };

  const handleSpeechToggle = () => {
    if (isSpeaking) {
      synth.cancel();
      speechQueue = [];
      setIsSpeaking(false);
    } else if (summaries[activeTab]) {
      synth.cancel();
      speechQueue = [];
      const plainText = summaries[activeTab].replace(/[*_#`]+/g, '');
      const chunks = plainText.match(/.{1,200}/g) || [plainText];
      let index = 0;

      const speakChunk = () => {
        if (index >= chunks.length) {
          setIsSpeaking(false);
          synth.cancel();
          speechQueue = [];
          return;
        }

        const utterance = new SpeechSynthesisUtterance(chunks[index]);
        utterance.rate = speechSpeed;
        utterance.volume = 1.0;
        if (selectedVoice) utterance.voice = selectedVoice;
        utterance.onend = () => {
          document.body.style.overflow = 'auto';
          index++;
          speakChunk();
        };
        utterance.onstart = () => {
          document.body.style.overflow = 'hidden';
        };
        setIsSpeaking(true);
        synth.speak(utterance);
      };

      if (synth.paused) synth.resume();
      speakChunk();
    }
  };

  const handleStop = () => {
    if (synth.speaking) {
      synth.cancel();
      speechQueue = [];
      setIsSpeaking(false);
    }
  };

  const handleZoomIn = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex < zoomLevels.length - 1) {
      const newZoomLevel = zoomLevels[currentIndex + 1];
      setZoomLevel(newZoomLevel);
      zoomPluginInstance.zoomTo(newZoomLevel);
    }
  };

  const handleZoomOut = () => {
    const currentIndex = zoomLevels.indexOf(zoomLevel);
    if (currentIndex > 0) {
      const newZoomLevel = zoomLevels[currentIndex - 1];
      setZoomLevel(newZoomLevel);
      zoomPluginInstance.zoomTo(newZoomLevel);
    }
  };

  useEffect(() => {
    const updateVoices = () => {
      const allVoices = synth.getVoices();
      setVoices(allVoices);
      const femaleVoice = allVoices.find(
        (voice) =>
          voice.name.toLowerCase().includes('female') ||
          voice.name.includes('Samantha') ||
          voice.name.includes('Victoria') ||
          voice.name.includes('Google US English')
      );
      if (femaleVoice) setSelectedVoice(femaleVoice);
    };

    synth.onvoiceschanged = updateVoices;
    updateVoices();
    return () => {
      synth.cancel();
      synth.onvoiceschanged = null;
    };
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  return (
    <div className="container-fluid">
      <style jsx>{`
        .rpv-default-layout__body {
          padding-top: 0.5rem;
        }
          
 .zoom-controls {
  display: flex;
  gap: 10px;

  padding: 10px 15px;
  border-radius: 8px;
  width: fit-content;
  height: 50px;
  align-items: center;
}


  .gemini-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 8px;
    background-color: #f8f9fa;
    border-top: 1px solid #ddd;
    font-size: 0.9rem;
    color: #6a737d;
    flex-shrink: 0;
  }

.zoom-btn {
  color: #black;
  border: none;
  padding: 8px 10px;
  font-size: 16px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-btn:hover {
  background-color: #1366d6;
}

.zoom-btn:disabled {
  background-color: #cbd5e0;
  cursor: not-allowed;
  color: #7a7a7a;
}

      `}</style>
      <div className="row g-3" style={{ flex: "1 0 auto", marginTop: "4%", paddingBottom: "40px", minHeight: "calc(100vh - 119.5px)" }}>
        {/* Left Column - Document Preview */}
        <div className="col-md-6 col-sm-12" style={{ height: "77vh", position: 'relative' }}>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="">Document Preview</h2>
            <div className="zoom-controls">
              <button
                className="zoom-btn"
                onClick={handleZoomOut}
                disabled={zoomLevel === zoomLevels[0]}
                title="Zoom Out"
              >
                <FaSearchMinus />
              </button>
              <button
                className="zoom-btn"
                onClick={handleZoomIn}
                disabled={zoomLevel === zoomLevels[zoomLevels.length - 1]}
                title="Zoom In"
              >
                <FaSearchPlus />
              </button>
            </div>
          </div>


          {docData?.filePath ? (
            <div className="pdf-container" style={{
              height: "calc(100% - 50px)",
              border: "1px solid #ddd",
              borderRadius: "5px",
              overflow: "hidden"
            }}>
              {/* Zoom Controls */}

              <div className="pdf-viewer" style={{ height: "100%", width: "100%" }}>
                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                  <Viewer
                    fileUrl={docData.filePath}
                    plugins={[defaultLayoutPluginInstance, zoomPluginInstance]}
                    initialScale={zoomLevel}
                    renderError={(error) => (
                      <div className="alert alert-danger">
                        <p>Failed to load PDF: {error.message}</p>
                        <p>Attempted URL: {docData.filePath}</p>
                        <a
                          href={docData.filePath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary mt-2"
                        >
                          Try Download Instead
                        </a>
                      </div>
                    )}
                  />
                </Worker>
              </div>

              {/* Gemini AI Footer */}

            </div>
          ) : (
            <div className="alert alert-warning h-100 d-flex align-items-center justify-content-center">
              {docData ? "No file path available" : "No document data found in session storage"}
            </div>
          )}

          <div className="gemini-footer">
            <SiGooglegemini color="#4285F4" size={16} />
            <span>Powered by Gemini AI</span>
          </div>
        </div>

        {/* Right Column - Summary Generation */}
        <div className="col-md-6 col-sm-12" style={{ height: "77vh" }}>
          <div className="d-flex justify-content-between align-items-center">
            <h2>Document Summary</h2>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={handleBack}
              style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
            >
              <FaArrowLeft /> Back
            </button>
          </div>
          <div style={{
            height: "calc(100% - 50px)",
            border: "1px solid #ddd",
            borderRadius: "5px",
            backgroundColor: "#f8f9fa",
            display: "flex",
            flexDirection: "column"
          }}>
            {/* Tabs */}
            <ul
              className="nav nav-tabs pb-0"
              style={{ paddingLeft: '20px', justifyContent: 'space-evenly', backgroundColor: '#f8f9fa', borderBottom: '1px solid #ddd' }}
            >
              {['short', 'medium', 'large'].map((tab) => (
                <li className="nav-item" key={tab}>
                  <button
                    className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                    onClick={() => handleTabChange(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                </li>
              ))}
            </ul>

            {/* Tab Content */}
            <div style={{
              flex: 1,
              overflowY: "auto",
              backgroundColor: "white",
              borderRadius: "0 0 5px 5px"
            }}>
              {summaries[activeTab] ? (
                <>
                  <MarkdownContainer>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        code({ node, inline, className, children, ...props }) {
                          const match = /language-(\w+)/.exec(className || '');
                          return !inline && match ? (
                            <SyntaxHighlighter
                              language={match[1]}
                              PreTag="div"
                              {...props}
                            >
                              {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                          ) : (
                            <code className={className} {...props}>
                              {children}
                            </code>
                          );
                        }
                      }}
                    >
                      {summaries[activeTab]}
                    </ReactMarkdown>

                    <div className="action-buttons">
                      <button
                        className="action-btn"
                        onClick={() => copyToClipboard(summaries[activeTab])}
                        title="Copy Summary"
                      >
                        <FaCopy /> Copy
                      </button>
                      <button
                        className="action-btn pdf-btn"
                        onClick={() => downloadAsPDF(summaries[activeTab], activeTab)}
                        title="Download as PDF"
                      >
                        <FaDownload /> PDF
                      </button>
                      <button
                        className="action-btn docx-btn"
                        onClick={() => downloadAsDocx(summaries[activeTab], activeTab)}
                        title="Download as DOCX"
                      >
                        <FaDownload /> DOCX
                      </button>
                      <div className="voice-controls">
                        <button
                          className={`action-btn speech-btn ${isSpeaking ? 'stop' : ''}`}
                          onClick={isSpeaking ? handleStop : handleSpeechToggle}
                          disabled={!summaries[activeTab]}
                          title={isSpeaking ? 'Stop Speech' : 'Play Speech'}
                        >
                          {isSpeaking ? <FaRegStopCircle /> : <LuSpeech />}
                        </button>
                        <select
                          value={speechSpeed}
                          onChange={(e) => setSpeechSpeed(parseFloat(e.target.value))}
                          className="form-select"
                        >
                          <option value={0.8}>Slow</option>
                          <option value={1.0}>Normal</option>
                          <option value={1.2}>Fast</option>
                        </select>
                      </div>
                    </div>
                  </MarkdownContainer>
                </>
              ) : (
                <div className="h-100 d-flex flex-column align-items-center justify-content-center">
                  <p className="text-muted">Click to generate {activeTab} summary</p>
                  <button
                    className="generate-btn btn btn-primary"
                    onClick={() => generateSummary(activeTab)}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Generating...
                      </>
                    ) : (
                      'Generate Summary'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentSummary;